import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { MANAGERIUM_QUERY_KEY, ONE_HOUR_MS } from '@/shared/api/queryClient'
import {
  getDashboardCard,
  type CardFilters,
  getDashboardStructure,
} from '@/features/dashboard/api/client'

/**
 * The only dashboard lionfish shows so far. It differs per Managerium
 * environment (live: 593, dev: 38), so it is set at build time. The default
 * matches the default Managerium URL, i.e. dev.
 */
const DASHBOARD_ID = Number(
  import.meta.env.VITE_MANAGERIUM_DASHBOARD_ID ?? '38'
)

const dashboardKeys = {
  all: [MANAGERIUM_QUERY_KEY, 'dashboard', DASHBOARD_ID] as const,
  card: (cardId: number, filters: CardFilters) =>
    [...dashboardKeys.all, 'card', cardId, filters] as const,
}

export const useDashboardStructure = () =>
  useQuery({
    queryKey: dashboardKeys.all,
    queryFn: () => getDashboardStructure(DASHBOARD_ID),
    staleTime: ONE_HOUR_MS,
  })

/**
 * A card, sliced by the filters if given. When they change the previous
 * numbers stay (`isPlaceholderData`) until the new ones arrive.
 */
export const useDashboardCard = (cardId: number, filters: CardFilters = {}) => {
  const { periods, markets } = filters
  const query = useQuery({
    queryKey: dashboardKeys.card(cardId, { periods, markets }),
    queryFn: () => getDashboardCard(DASHBOARD_ID, cardId, { periods, markets }),
    staleTime: ONE_HOUR_MS,
    placeholderData: keepPreviousData,
  })
  return { ...query, isRefreshing: query.isFetching && query.isPlaceholderData }
}
